import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { QueryParams } from '@/model';
import { WonTitleService } from '../instances/won-title';
import { WonTitleConverter } from '@/converters';

export const useWonTitle = (params?: QueryParams) => {
  const query = useQuery(
    ['GET_WON_TITLE', params],
    async () => {
      const [err, data] = await WonTitleService.getWonTitles(params);
      if (err) {
        throw err;
      }
      return {
        data: WonTitleConverter.toDomainFromPaginatedResponse(data),
        meta: data.meta,
      };
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const useWonTitleById = (id: string) => {
  const query = useQuery(
    ['GET_WON_TITLE_BY_ID', id],
    async () => {
      const [err, data] = await WonTitleService.getWonTitleById(id);
      if (err) {
        throw err;
      }
      return WonTitleConverter.toDomainFromByIdResponse(data);
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!id,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const usePostWonTitleMutation = () => {
  return useMutation({
    mutationKey: ['POST_WON_TITLE'],
    mutationFn: async (payload: any) => {
      const [err, data] = await WonTitleService.postWonTitle(payload);
      if (err) {
        throw err;
      }
      return WonTitleConverter.toDomainFromPostResponse(data);
    },
  });
};

export const usePutWonTitleMutation = () => {
  return useMutation({
    mutationKey: ['PUT_WON_TITLE'],
    mutationFn: async ({ id, payload }: any) => {
      const [err, data] = await WonTitleService.putWonTitle(id, payload);
      if (err) {
        throw err;
      }
      return WonTitleConverter.toDomainFromPutResponse(data);
    },
  });
};

export const useDeleteWonTitleMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_WON_TITLE'],
    mutationFn: async (id: string) => {
      const [err] = await WonTitleService.deleteWonTitle(id);
      if (err) {
        throw err;
      }
      return;
    },
  });
};

export const useGetWonTitleById = () => {
  return useMutation({
    mutationKey: ['GET_WON_TITLE_BY_ID'],
    mutationFn: async (id: string) => {
      const [err, data] = await WonTitleService.getWonTitleById(id);
      if (err) {
        throw err;
      }
      return WonTitleConverter.toDomainFromByIdResponse(data);
    },
  });
};
